import { useRef, useState } from "react"
import { useEntidad, useLista, useManagedContext } from "../../components/hooks"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { onRequestError, onRequestNoSuccess } from "../../utils/requests"
import { getDateToString, getFormatNumber, precisionRound } from "../../utils/convert"
import CuentaPlanPagoModal from "../../components/controls/CuentaPlanPagoModal"
import { TIPS } from "../../consts/tips"


const EstadoPlanesPagoView = () => {
    const { setIsLoading } = useManagedContext()

    const searchRef = useRef()

    const [planesPago, setPlanesPago] = useState([])
    const [tableVisible, setTableVisible] = useState(false)
    const [selectedPlanPagoId, setSelectedPlanPagoId] = useState(null)

    const [, getRowLista] = useLista({
        listas: ['TipoTributo', 'EstadoPlanPagoCuota'],
        memo: {
          key: 'TipoTributo_EstadoPlanPagoCuota',
          timeout: 0
        }
    });

    const [, _getEntidad] = useEntidad({
        entidades: ['TipoPlanPago'],
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        }
    })
    const getEntidad = (type, id) => _getEntidad(type, id) ?? {}

    const getDescTipoTributo = (idTipoTributo) => {
        const row = getRowLista('TipoTributo', idTipoTributo);
        return row ? row.nombre : "";
    }

    const getImporteTotal = (row) => {
        return precisionRound(row.importePlanPago+row.importeSellados);
    }

    const SearchPlanesPago = () => {
        const { idTipoTributo, idCuenta, idTipoPlanPago, idTasaPlanPago, idSubTasaPlanPago, idEstadoPlanPagoCuota, fechaDesde, fechaHasta, codigo, numero } = searchRef.current.filters

        setIsLoading(true)

        const params = new URLSearchParams()

        if (idTipoTributo) params.append('idTipoTributo', idTipoTributo)
        if (idCuenta) params.append('idCuenta', idCuenta)
        if (idTipoPlanPago) params.append('idTipoPlanPago', idTipoPlanPago)
        if (idTasaPlanPago) params.append('idTasaPlanPago', idTasaPlanPago)
        if (idSubTasaPlanPago) params.append('idSubTasaPlanPago', idSubTasaPlanPago)
        if (idEstadoPlanPagoCuota) params.append('idEstadoPlanPagoCuota', idEstadoPlanPagoCuota)
        if (fechaDesde) params.append('fechaDesde', fechaDesde.toISOString())
        if (fechaHasta) params.append('fechaHasta', fechaHasta.toISOString())
        if (codigo) params.append('codigo', codigo)
        if (numero) params.append('numero', numero)

        const paramsUrl = `/filter?${params.toString()}`

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PLAN_PAGO,
            paramsUrl,
            null,
            res => res.json().then(data => {
                setPlanesPago(data)
                setTableVisible(true)
                setIsLoading(false)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const avmr = {
        onView: (item) => {
            setSelectedPlanPagoId(item.id)
        }
    }

    return <>
        <SectionHeading titles={[{ title: 'Financiación' }, { title: 'Estado de Planes de Pago' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                requireFilter={true}
                filters={[
                    { title: 'Convenio', tipName: TIPS.NAME.MODALS.TIPO_PLAN_PAGO_MODAL, field: 'idTipoPlanPago', type: 'entity', entidad: 'TipoPlanPago', size: 4 },
                    { title: 'Tipo de tributo', field: 'idTipoTributo', type: 'list', lista: 'TipoTributo', size: 2 },
                    { title: 'Cuenta', field: 'idCuenta', type: 'cuenta', size: 2 },
                    { title: 'Estado', field: 'idEstadoPlanPagoCuota', type: 'list', lista: 'EstadoPlanPagoCuota', size: 2 },
                    { title: 'Tasa del plan de pago', field: 'idTasaPlanPago', type: 'tasa', size: 4 },
                    { title: 'Sub tasa del plan de pago', field: 'idSubTasaPlanPago', type: 'subtasa', fieldTasa: 'idTasaPlanPago', size: 4 },
                    { title: 'Fecha desde', field: 'fechaDesde', type: 'date', size: 2 },
                    { title: 'Fecha hasta', field: 'fechaHasta', type: 'date', size: 2, minValueField: 'fechaDesde' },
                    { title: 'Código', field: 'codigo', type: 'string', size: 4 },
                    { title: 'Número', field: 'numero', type: 'string', size: 2 },
                ]}
                onSearch={SearchPlanesPago}
                onClear={() => {
                    setPlanesPago([])
                    setTableVisible(false)
                }}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    data={planesPago}
                    avmr={avmr}
                    messageEmpty="No se encontraron resultados"
                    columns={[
                        { Header: 'Tributo', Cell: (props) => getDescTipoTributo(props.value), accessor: 'idTipoTributo', width: '7%' },
                        { Header: 'Cuenta', accessor: 'numeroCuenta', width: '8%', alignCell: 'right' },
                        { Header: 'Número', accessor: 'numero', width: '8%', alignCell: 'right' },
                        { Header: 'Código', accessor: 'codigo', width: '7%', alignCell: 'right' },
                        { Header: 'Descripción', accessor: 'descripcion', width: '24%' },
                        { Header: 'Fecha', Cell: (props) => (props.value) ? getDateToString(props.value, false) : '', accessor: 'fechaRegistro', width: '8%' },
                        { Header: 'Estado', Cell: (props) => getRowLista('EstadoPlanPagoCuota', props.value)?.nombre ?? '', accessor: 'idEstadoPlanPagoCuota', width: '8%' },
                        { Header: 'Importe total', Cell: (props) => getFormatNumber(getImporteTotal(props.row.original), 2), accessor: 'importePlanPago', width: '11%', alignCell: 'right' },
                        { Header: () => <span title="Deuda judicializada">Deuda judicializada</span>, csvHeader: 'Deuda judicializada', Cell: (props) => props.row.original.tieneDeudaLegal ? 'Sí' : 'No', id: 'legalizada', accessor: 'tieneDeudaLegal', width: '15%' },
                    ]}
                />
            )}

            {selectedPlanPagoId && (
                <CuentaPlanPagoModal
                    idPlanPago={selectedPlanPagoId}
                    onDismiss={() => setSelectedPlanPagoId(null)}
                />
            )}

        </section>
    </>
}

export default EstadoPlanesPagoView
